import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaPhone, FaWhatsapp } from 'react-icons/fa';
import {
  ChannelsSection,
  ChannelsTitle,
  ChannelsContainer,
  ChannelItem,
  ChannelIconContainer,
  ChannelIcon,
  ChannelName,
  DataParticle
} from '../../components/ChannelsStyles';

// Animações
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.5), 0 0 10px rgba(100, 182, 255, 0.3);
    border-color: rgba(100, 182, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 25px rgba(100, 182, 255, 0.8), 0 0 40px rgba(100, 182, 255, 0.5);
    border-color: rgba(0, 255, 255, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.5), 0 0 10px rgba(100, 182, 255, 0.3);
    border-color: rgba(100, 182, 255, 0.3);
  }
`;

const digitalScan = keyframes`
  0% {
    opacity: 0;
    height: 1px;
    top: 0%;
  }
  20% {
    opacity: 0.8;
  }
  50% {
    height: 3px;
  }
  80% {
    opacity: 0.8;
  }
  100% {
    opacity: 0;
    height: 1px;
    top: 100%;
  }
`;

const techGridPulse = keyframes`
  0% {
    opacity: 0.1;
    background-size: 30px 30px;
  }
  50% {
    opacity: 0.4;
    background-size: 40px 40px;
  }
  100% {
    opacity: 0.1;
    background-size: 30px 30px;
  }
`;

const dataFlowPulse = keyframes`
  0% {
    background-position: 0% 0%;
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    background-position: 200% 200%;
    opacity: 0.3;
  }
`;

const cornerPulse = keyframes`
  0% {
    opacity: 0.4;
    width: 30px;
    height: 30px;
  }
  50% {
    opacity: 0.9;
    width: 50px;
    height: 50px;
  }
  100% {
    opacity: 0.4;
    width: 30px;
    height: 30px;
  }
`;

const matrixReveal = keyframes`
  0% {
    opacity: 0;
    transform: perspective(1000px) rotateX(10deg) translateY(50px);
    clip-path: polygon(0 0, 100% 0, 100% 0, 0 0);
  }
  30% {
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
  }
  60% {
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
  }
  100% {
    opacity: 1;
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
`;

const circleExpand = keyframes`
  0% {
    clip-path: circle(0% at center);
    opacity: 0;
  }
  100% {
    clip-path: circle(150% at center);
    opacity: 1;
  }
`;

const floatAnimation = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-8px) rotate(2deg);
  }
  66% {
    transform: translateY(4px) rotate(-2deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const energyPulse = keyframes`
  0% {
    filter: drop-shadow(0 0 2px rgba(100, 182, 255, 0.5));
  }
  50% {
    filter: drop-shadow(0 0 15px rgba(0, 255, 255, 0.8));
  }
  100% {
    filter: drop-shadow(0 0 2px rgba(100, 182, 255, 0.5));
  }
`;

const glowText = keyframes`
  0% {
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
    letter-spacing: 1px;
  }
  50% {
    text-shadow: 0 0 20px rgba(0, 255, 255, 0.8), 0 0 30px rgba(0, 255, 255, 0.6);
    letter-spacing: 2px;
  }
  100% {
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
    letter-spacing: 1px;
  }
`;

const rotateIn = keyframes`
  0% {
    transform: rotate(180deg) scale(0);
    opacity: 0;
  }
  100% {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
`;

const particleFlow = keyframes`
  0% {
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-300px) translateX(100px) scale(0);
    opacity: 0;
  }
`;

const holographicShimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const energyBurst = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
    filter: blur(10px);
  }
  50% {
    opacity: 0.8;
    filter: blur(5px);
  }
  100% {
    opacity: 0;
    transform: scale(1.4);
    filter: blur(0px);
  }
`;

const iconPop = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const phoneIconPop = keyframes`
  0% {
    transform: scaleX(-1) scale(1);
  }
  50% {
    transform: scaleX(-1) scale(1.2);
  }
  100% {
    transform: scaleX(-1) scale(1);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      linear-gradient(45deg, rgba(100, 182, 255, 0.05) 1px, transparent 1px),
      linear-gradient(135deg, rgba(100, 182, 255, 0.05) 1px, transparent 1px);
    background-size: 30px 30px;
    animation: ${techGridPulse} 8s ease infinite;
    pointer-events: none;
    z-index: 1;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: transparent;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 3px;
  font-size: 2.2rem;
  text-transform: uppercase;
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, #64B6FF, #00FFFF, #90CAFF);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  background-size: 200% auto;
  animation: ${holographicShimmer} 6s linear infinite;
  font-weight: 600;
  z-index: 10;
  
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    width: 100px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8), transparent);
    left: 50%;
    transform: translateX(-50%);
  }
  
  &::before {
    top: -10px;
  }
  
  &::after {
    bottom: -10px;
  }
`;

const TitleCodeDecoration = styled.span`
  position: absolute;
  font-family: monospace;
  font-size: 12px;
  color: rgba(100, 182, 255, 0.5);
  opacity: 0.6;
  
  &.top-left {
    top: -10px;
    left: 20%;
    &::before {
      content: '/* channels.initialize() */';
    }
  }
  
  &.top-right {
    top: -10px;
    right: 20%;
    &::before {
      content: '/* bianca.core */';
    }
  }
`;

const ContentPreview = styled.div`
  max-width: 1200px;
  width: 90%;
  height: 65vh;
  margin: 0 auto;
  padding: 60px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 15px;
  background: rgba(5, 10, 25, 0.7);
  box-shadow: 0 0 30px rgba(100, 182, 255, 0.15);
  backdrop-filter: blur(10px);
  position: relative;
  animation: ${matrixReveal} 1.2s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8), transparent);
    animation: ${pulseGlow} 5s infinite;
  }
  
  &::after {
    top: auto;
    bottom: 0;
    animation-delay: 1.5s;
  }
`;

const HologramEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(100, 182, 255, 0.03) 0%, rgba(0, 200, 255, 0.01) 50%, rgba(100, 182, 255, 0.03) 100%);
  pointer-events: none;
  z-index: 3;
`;

const DigitalScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8), transparent);
  z-index: 4;
  opacity: 0;
  animation: ${digitalScan} 5s ease-in-out infinite;
`;

const TechCorners = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 50px;
    height: 50px;
    border-radius: 0;
    animation: ${cornerPulse} 4s ease-in-out infinite;
  }
  
  /* Top left corner */
  &::before {
    top: 0;
    left: 0;
    border-top: 2px solid rgba(100, 182, 255, 0.8);
    border-left: 2px solid rgba(100, 182, 255, 0.8);
  }
  
  /* Bottom right corner */
  &::after {
    bottom: 0;
    right: 0;
    border-bottom: 2px solid rgba(100, 182, 255, 0.8);
    border-right: 2px solid rgba(100, 182, 255, 0.8);
  }
`;

const DataLinesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  opacity: 0.5;
  pointer-events: none;
`;

const DataLine = styled.div`
  position: absolute;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.2), transparent);
  height: 1px;
  width: 100%;
  transform: translateY(${props => props.yPos}px);
  animation: ${dataFlowPulse} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  opacity: 0.5;
  
  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    background-color: rgba(0, 255, 255, 0.8);
    border-radius: 50%;
    left: ${props => props.dotPos}%;
    top: -3px;
    box-shadow: 0 0 10px 2px rgba(0, 255, 255, 0.4);
    opacity: 0.7;
  }
`;

const EnhancedChannelsTitle = styled(ChannelsTitle)`
  font-size: 2.5rem;
  margin-bottom: 50px;
  animation: ${glowText} 5s infinite;
  background: linear-gradient(90deg, #64B6FF, #00FFFF, #64B6FF);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  animation: ${circleExpand} 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards 0.5s, 
            ${holographicShimmer} 5s linear infinite 0.8s;
`;

const EnhancedChannelsContainer = styled(ChannelsContainer)`
  width: 100%;
  display: flex;
  justify-content: space-around;
  gap: 120px;
  z-index: 5;
  position: relative;
`;

const EnhancedChannelItem = styled(ChannelItem)`
  width: 320px;
  height: 320px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(5, 10, 25, 0.8);
  position: relative;
  opacity: 0;
  animation: ${rotateIn} 1.5s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  animation-delay: ${props => props.delay || '0s'};
  will-change: transform;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    z-index: -1;
  }
  
  &::before {
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border: 2px solid ${props => props.borderColor || 'rgba(100, 182, 255, 0.3)'};
    animation: ${pulseGlow} 5s infinite;
    transition: all 0.5s ease;
  }
  
  &::after {
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 1px solid ${props => props.accentColor || 'rgba(0, 255, 255, 0.5)'};
    opacity: 0.8;
    transition: all 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-20px) scale(1.1);
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    z-index: 10;
    
    &::before {
      box-shadow: 0 0 50px ${props => props.accentColor || 'rgba(0, 255, 255, 0.7)'};
      border-color: ${props => props.accentColor || 'rgba(0, 255, 255, 1)'};
      border-width: 3px;
      top: -12px;
      left: -12px;
      right: -12px;
      bottom: -12px;
    }
    
    &::after {
      opacity: 1;
      border-width: 2px;
    }
    
    .channel-hover-effect {
      animation: ${energyBurst} 1.2s ease-out;
    }
    
    .channel-name {
      letter-spacing: 4px;
      text-shadow: 0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 0.8)'};
    }
    
    .whatsapp-icon {
      animation: ${iconPop} 0.5s ease forwards;
      filter: drop-shadow(0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 1)'});
    }
    
    .phone-icon {
      animation: ${phoneIconPop} 0.5s ease forwards;
      filter: drop-shadow(0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 1)'});
    }
    
    .tech-circle {
      border-color: ${props => props.accentColor || 'rgba(0, 255, 255, 0.7)'};
      opacity: 0.8;
    }
    
    .orbit-circle {
      opacity: 0.9;
      border-width: 2px;
    }
    
    .orbit-dot {
      transform: scale(1.5) translateY(-50%);
      box-shadow: 0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 1)'};
    }
  }
`;

const CircleOrbit = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  border: 1px dashed ${props => props.color || 'rgba(100, 182, 255, 0.2)'};
  opacity: 0.5;
  animation: spin ${props => props.duration}s linear infinite;
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const OrbitDot = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: ${props => props.color || '#00FFFF'};
  border-radius: 50%;
  top: 0;
  left: calc(50% - 5px);
  transform: translateY(-50%);
  box-shadow: 0 0 10px ${props => props.color || '#00FFFF'};
  animation: ${energyPulse} 3s infinite;
`;

const EnhancedChannelIconContainer = styled(ChannelIconContainer)`
  width: 150px;
  height: 150px;
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  animation: ${floatAnimation} 6s ease-in-out infinite;
  transition: all 0.4s ease;
`;

const EnhancedChannelIcon = styled(ChannelIcon)`
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: rgba(5, 10, 25, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -10%;
    left: -10%;
    width: 120%;
    height: 120%;
    background: linear-gradient(
      135deg, 
      rgba(255, 255, 255, 0.02) 0%, 
      rgba(255, 255, 255, 0.1) 50%, 
      rgba(255, 255, 255, 0.02) 100%
    );
    opacity: 0.5;
    z-index: 1;
    pointer-events: none;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  
  svg {
    width: 75px;
    height: 75px;
    animation: ${energyPulse} 4s infinite;
    filter: drop-shadow(0 0 10px ${props => props.color || 'rgba(0, 255, 255, 0.8)'});
    transition: filter 0.4s ease, transform 0.4s ease;
  }
`;

const EnhancedChannelName = styled(ChannelName)`
  font-size: 32px;
  margin-top: 25px;
  background: linear-gradient(90deg, ${props => props.startColor || '#64B6FF'}, ${props => props.endColor || '#00FFFF'});
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
  letter-spacing: 2px;
  animation: ${holographicShimmer} 5s linear infinite;
  transition: letter-spacing 0.4s ease, text-shadow 0.4s ease;
  position: relative;
`;

const TechCircle = styled.div`
  position: absolute;
  width: ${props => props.size || '380px'};
  height: ${props => props.size || '380px'};
  border-radius: 50%;
  border: 1px solid ${props => props.color || 'rgba(100, 182, 255, 0.2)'};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.5;
  z-index: 1;
`;

const HoverEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${props => props.color || 'rgba(0, 255, 255, 0.4)'} 0%,
    transparent 70%
  );
  opacity: 0;
  z-index: 0;
  pointer-events: none;
`;

const EnhancedDataParticle = styled(DataParticle)`
  width: ${props => props.size || '8px'};
  height: ${props => props.size || '8px'};
  box-shadow: 0 0 15px ${props => props.color || '#64B6FF'};
  position: absolute;
  top: ${props => props.top || '50%'};
  left: ${props => props.left || '50%'};
  border-radius: 50%;
  z-index: 2;
  opacity: 0;
  animation: ${particleFlow} ${props => props.duration || '5s'} linear infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

const PresentationSlide4 = () => {
  const [dataLines, setDataLines] = useState([]);
  
  useEffect(() => {
    // Gerar linhas de dados aleatórias
    const lines = [];
    const lineCount = 10;
    
    for (let i = 0; i < lineCount; i++) {
      const yPos = Math.floor(Math.random() * 100) * 6;
      const duration = 10 + Math.random() * 15;
      const delay = Math.random() * 5;
      const dotPos = Math.random() * 100;
      
      lines.push({ yPos, duration, delay, dotPos });
    }
    
    setDataLines(lines);
  }, []);

  // Função para gerar partículas de dados aleatórias
  const renderDataParticles = (count, containerIndex) => {
    const particles = [];
    const colors = [
      '#00FFFF', 
      '#64B6FF', 
      '#0080FF', 
      '#FFFFFF',
      '#90CAFF',
      '#00FFAA'
    ];
    const sizes = ['4px', '6px', '8px', '10px'];
    
    for (let i = 0; i < count; i++) {
      const top = `${Math.random() * 100}%`;
      const left = `${Math.random() * 100}%`;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = sizes[Math.floor(Math.random() * sizes.length)];
      const duration = `${3 + Math.random() * 8}s`;
      const delay = `${Math.random() * 5}s`;
      
      particles.push(
        <EnhancedDataParticle 
          key={`particle-${containerIndex}-${i}`}
          top={top}
          left={left}
          color={color}
          size={size}
          duration={duration}
          delay={delay}
        />
      );
    }
    
    return particles;
  };

  return (
    <SlideWrapper>
      <SlideTitle>
        Canais de Atendimento
        <TitleCodeDecoration className="top-left" />
        <TitleCodeDecoration className="top-right" />
      </SlideTitle>
      
      <ContentPreview>
        <HologramEffect />
        <DigitalScanLine />
        
        <TechCorners />
        
        <DataLinesContainer>
          {dataLines.map((line, index) => (
            <DataLine 
              key={index}
              yPos={line.yPos}
              duration={line.duration}
              delay={line.delay}
              dotPos={line.dotPos}
            />
          ))}
        </DataLinesContainer>
        
        <EnhancedChannelsContainer>
          {/* Canal WhatsApp */}
          <EnhancedChannelItem 
            delay="0.5s" 
            borderColor="rgba(37, 211, 102, 0.5)" 
            accentColor="rgba(37, 211, 102, 0.8)"
          >
            {renderDataParticles(15, 0)}
            <HoverEffect className="channel-hover-effect" color="rgba(37, 211, 102, 0.4)" />
            
            <CircleOrbit className="orbit-circle" size={390} color="rgba(37, 211, 102, 0.2)" duration={40}>
              <OrbitDot className="orbit-dot" color="#25D366" />
            </CircleOrbit>
            <CircleOrbit className="orbit-circle" size={360} color="rgba(37, 211, 102, 0.15)" duration={30} style={{ transform: 'rotate(45deg)' }}>
              <OrbitDot className="orbit-dot" color="#25D366" />
            </CircleOrbit>
            
            <TechCircle className="tech-circle" size="350px" color="rgba(37, 211, 102, 0.2)" />
            
            <EnhancedChannelIconContainer>
              <EnhancedChannelIcon>
                <IconWrapper className="icon-wrapper" color="rgba(37, 211, 102, 0.8)">
                  <FaWhatsapp className="whatsapp-icon" style={{ color: '#25D366' }} />
                </IconWrapper>
              </EnhancedChannelIcon>
            </EnhancedChannelIconContainer>
            
            <EnhancedChannelName className="channel-name" startColor="#25D366" endColor="#30E675">
              WhatsApp
            </EnhancedChannelName>
          </EnhancedChannelItem>
          
          {/* Canal Ligação */}
          <EnhancedChannelItem 
            delay="0.8s" 
            borderColor="rgba(100, 182, 255, 0.5)" 
            accentColor="rgba(0, 255, 255, 0.8)"
          >
            {renderDataParticles(15, 1)}
            <HoverEffect className="channel-hover-effect" color="rgba(0, 255, 255, 0.4)" />
            
            <CircleOrbit className="orbit-circle" size={390} color="rgba(100, 182, 255, 0.2)" duration={35}>
              <OrbitDot className="orbit-dot" color="#64B6FF" />
            </CircleOrbit>
            <CircleOrbit className="orbit-circle" size={360} color="rgba(100, 182, 255, 0.15)" duration={25} style={{ transform: 'rotate(135deg)' }}>
              <OrbitDot className="orbit-dot" color="#64B6FF" />
            </CircleOrbit>
            
            <TechCircle className="tech-circle" size="350px" color="rgba(100, 182, 255, 0.2)" />
            
            <EnhancedChannelIconContainer>
              <EnhancedChannelIcon>
                <IconWrapper className="icon-wrapper" color="rgba(100, 182, 255, 0.8)">
                  <FaPhone className="phone-icon" style={{ transform: 'scaleX(-1)', color: '#64B6FF' }} />
                </IconWrapper>
              </EnhancedChannelIcon>
            </EnhancedChannelIconContainer>
            
            <EnhancedChannelName className="channel-name" startColor="#64B6FF" endColor="#00FFFF">
              Ligação
            </EnhancedChannelName>
          </EnhancedChannelItem>
        </EnhancedChannelsContainer>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide4;
